import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { createApp } from 'vue'
import App from './App.vue'
// Axios is used to fetch data.
import axios from 'axios'
import VueAxios from 'vue-axios'
import mixpanel from 'mixpanel-browser'
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent'

// 🔑 Initialize Mixpanel
mixpanel.init('7698ec337301c5e653d956fee74ae79b', {
  debug: true,
  autocapture: true
})
// Detect environment from URL
function getEnvironment() {
  const host = window.location.hostname;

  if (host.includes('localhost') || host.includes('127.0.0.1') || host.includes('dev')) {
    return 'dev';
  } else if (host.includes('stage') || host.includes('staging') || host.includes('stg')) {
    return 'stage';
  } else {
    return 'prod';
  }
}

function getUtmParameters() {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {};

  // redirected flag
  const redirected = urlParams.get('utm_source') ? 'true' : 'false';
  utmParams['redirected'] = redirected;

  // standard UTM params
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach(param => {
    const value = urlParams.get(param);
    utmParams[param] = value ? value : 'NA';
  });

  // unify cl_item and cl_items
  const clItem = urlParams.get('cl_item');
  const clItems = urlParams.get('cl_items');

  if (clItem || clItems) {
    // collect both if present
    const combined = [clItems, clItem]
      .filter(Boolean)                // remove nulls
      .map(v => v.replace(/\|/g, ',')) // replace pipes with commas
      .join(',');                      // merge into one string
    utmParams['cl_items'] = combined;
    console.log('Combined cl_items:', combined);
  } else {
    utmParams['cl_items'] = 'NA'; 
  }

  return utmParams;
}


// Register UTM parameters if present
const utmParams = getUtmParameters();
mixpanel.register(utmParams);

// Register environment property 
const env = getEnvironment();
mixpanel.register({ environment: env });

// Track Exit_Page
window.addEventListener('beforeunload', () => {
  const now = new Date();
  mixpanel.track('Exit_Page', {
    page: window.location.pathname,
    dateVisited: now.toLocaleDateString(),
    timestamp: now.toISOString()
  });
});

// Track Scroll_Depth_Reached
window.addEventListener('scroll', () => {
  const scrollDepth = Math.round((window.scrollY / document.body.scrollHeight) * 100);
  if (scrollDepth >= 50) {
    const now = new Date();
    mixpanel.track('Scroll_Depth_Reached', {
      depth: scrollDepth,
      dateVisited: now.toLocaleDateString(),
      timestamp: now.toISOString()
    });
  }
});

import apiPropertyFetcher from './apiPropertyFetcher'
import { validateAndLog } from '@/utils/validation'

// These styles load Tailwind CSS styles.
import './assets/styles/index.css'

// This pattern has been modified so we can alter the routes.
import { createRouter, createWebHashHistory } from 'vue-router'
import routes from './router'
import { validateUUIDParam } from './router/guards'

// See `public/index.html` for an example of the embeddable code.
const appID = 'content-library'
const appElement = document.getElementById(appID)

// new Relic initialization
let newRelicAgent = null
if (process.env.VUE_APP_NEW_RELIC_ENABLED === 'true') {
  const options = {
    init: {
      distributed_tracing: { enabled: true },
      privacy: { cookies_enabled: true },
      ajax: {
        deny_list: [],
        enabled: true
      },
      session_replay: {
        enabled: false, // Enable after testing
        sampling_rate: 10.0,
        error_sampling_rate: 100.0
      },
      custom_attributes: {
        environment: env
      },
      feature_flags: ['spa', 'ajax']
    },
    info: {
      beacon: 'bam.nr-data.net',
      errorBeacon: 'bam.nr-data.net',
      licenseKey: process.env.VUE_APP_NEW_RELIC_LICENSE_KEY,
      applicationID: process.env.VUE_APP_NEW_RELIC_APP_ID,
      sa: 1
    },
    loader_config: {
      accountID: process.env.VUE_APP_NEW_RELIC_ACCOUNT_ID,
      trustKey: process.env.VUE_APP_NEW_RELIC_TRUST_KEY,
      agentID: process.env.VUE_APP_NEW_RELIC_APP_ID,
      licenseKey: process.env.VUE_APP_NEW_RELIC_LICENSE_KEY,
      applicationID: process.env.VUE_APP_NEW_RELIC_APP_ID
    }
  }

  try {
    newRelicAgent = new BrowserAgent(options)
  } catch (error) {
    console.error('Failed to initialize New Relic:', error)
  }
}

// Make New Relic available globally for custom instrumentation
if (newRelicAgent) {
  window.newrelic = newRelicAgent
}
if (window.newrelic) {
  try {
    console.log('Setting New Relic environment attribute:', env);
    window.newrelic.setCustomAttribute('environment', env);
  } catch (e) {
    console.warn('Failed to set New Relic environment attribute', e);
  }
}

function trackNewRelicAction(name, attributes = {}) {
  if (!window.newrelic) return
  
  try {
    const interaction = window.newrelic.interaction()
    interaction.setName(name)
    interaction.setAttribute('environment', env);

    // Attach attributes
    Object.entries(attributes).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        interaction.setAttribute(key, value)
      }
    })

    interaction.save()
  } catch (error) {
    console.error('New Relic interaction error:', error)
  }
}
 
// Example: track page content loaded
trackNewRelicAction('contentLoaded', {
  contentType: 'article',
  loadTime: performance.now()
})


async function getHomeRoute() {
  let homeRoute = {
    path: '/',
    name: 'Home',
  }

  // Setting the route using query parameters:
  // Node ID: Pass `cl_type=node` and `cl_item={node id}`
  // Node IDs: Pass `cl_type=prescribed` and `cl_items={node id 1}|{node id 2}|...`
  // Type and UUID: Pass `cl_type={content library type}` and `cl_uuid`
  const urlParams = new URLSearchParams(window.location.search)
  const initialQuery = Object.fromEntries(urlParams)

  if (typeof initialQuery.cl_type === 'undefined') {
    homeRoute.component = () => import('@/components/ContentCollection.vue')
    homeRoute.props = {
      uuid: appElement.getAttribute('data-id'),
    }
  }
  else if (initialQuery.cl_type === 'prescribed' && typeof initialQuery.cl_items !== 'undefined') {
    let items = []

    await Promise
      .all(
        initialQuery.cl_items.split('|').map(( nodeID ) => {
          // Validate node ID and skip invalid ones
          const validation = validateAndLog('nodeId', nodeID);
          if (!validation.valid) {
            console.error('Invalid node ID:', nodeID);
            return null;
          }
          return apiPropertyFetcher({ nodeID })
        })
      )
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('error: ', error)
        }
      })
      .then(( data ) => {
        items = data
          .filter((element) => typeof element === 'object')
          .map(({ uuid, contentLibraryType }) => {
            return {
              uuid,
              type: contentLibraryType,
            }
          })

        return data
      })

    if (items.length > 0) {
      homeRoute.component = () => import('@/components/ContentList.vue')
      homeRoute.props = {
        items,
      }
    }
  }
  else if (initialQuery.cl_type === 'node' && typeof initialQuery.cl_item !== 'undefined') {
    // Validate node ID first
    const validation = validateAndLog('nodeId', initialQuery.cl_item);
    if (!validation.valid) {
      homeRoute.component = () => import('@/components/ErrorPage.vue')
      homeRoute.props = {
        message: 'Invalid node ID provided.',
      }
      return homeRoute
    }
    
    await apiPropertyFetcher({ nodeID: initialQuery.cl_item })
      .then( ({ uuid, contentLibraryType }) => {
        // Determine the component from the routes.
        routes.forEach((route) => {
          if (route.path.split('/')[1] === contentLibraryType) {
            homeRoute.component = route.component
          }
        })

        homeRoute.props = {
          uuid,
        }
      })
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('error: ', error)
        }
      })
  }
  // The use case where a type matches the Content Library type and a UUID is provided.
  else if (typeof initialQuery.cl_uuid !== 'undefined') {
    let uuid = initialQuery.cl_uuid
    let type = initialQuery.cl_type
    
    // Validate UUID and block if invalid
    const validation = validateAndLog('uuid', uuid)
    if (!validation.valid) {
      homeRoute.component = () => import('@/components/ErrorPage.vue')
      homeRoute.props = {
        message: 'Invalid UUID provided.',
      }
      return homeRoute
    }

    // Determine the component from the routes.
    routes.forEach((route) => {
      if (route.path.split('/')[1] === type) {
        homeRoute.component = route.component
      }
    })

    homeRoute.props = {
      uuid: uuid,
    }
  }
  
  if (typeof homeRoute.component === 'undefined') {
    homeRoute.component = () => import('@/components/ErrorMessage.vue')
    homeRoute.props = {
      message: 'Content could not be found.',
    }
  }

  return homeRoute
}

// We are using promises to parse the various entrypoints, but we need to wait on those in order to create the application.
const initializeApp = async () => {
  // Add error route
  routes.push({
    path: '/error',
    name: 'Error',
    component: () => import('@/components/ErrorPage.vue'),
    props: true
  })
  
  routes.push(await getHomeRoute())

  const router = createRouter({
    history: createWebHashHistory(),
    routes
  })

  // Add global navigation guard for UUID validation
  router.beforeEach(validateUUIDParam)

  // If we want to pass properties to the application, such as a theme class, we can do so here.
  const rootProperties = {}
  const app = createApp(App, rootProperties)

  // Attach Mixpanel to global properties
  app.config.globalProperties.$mixpanel = mixpanel

  // Track Page_Viewed, Navigation_Sequence, and Date_Viewed
  router.afterEach((to, from) => {
    const now = new Date();
    const dateVisited = now.toLocaleDateString();
    const timestamp = now.toISOString();

    // Page Viewed
    mixpanel.track('Page_Viewed', {
      page: to.fullPath,
      from: from.fullPath,
      dateVisited,
      timestamp
    });

    // Navigation Sequence
    mixpanel.track('Navigation_Sequence', {
      from: from.fullPath,
      to: to.fullPath,
      dateVisited,
      timestamp
    });

    // Dedicated Date Viewed event
    mixpanel.track('Date_Viewed', {
      page: to.fullPath,
      dateVisited,
      timestamp
    });
  });


  router.afterEach((to, from) => {
  // New Relic route tracking
if (window.newrelic) {
    try {
      const attrs = {
        routeName: to.name || null,
        routePath: to.fullPath,
        fromPath: from.fullPath,
        timestamp: new Date().toISOString()
      }

      const interaction = window.newrelic.interaction().setName(`Route: ${to.name || to.path}`)

      if (typeof interaction.setAttribute === 'function') {
        Object.entries(attrs).forEach(([k, v]) => {
          if (v !== null && v !== undefined) interaction.setAttribute(k, v)
        })
      }

      interaction.save()
      window.newrelic.setPageViewName(to.name || to.path)
      window.newrelic.addPageAction('route_change', {
      routeName: to.name,
      routePath: to.fullPath,
      fromPath: from.fullPath
  })
    } catch (e) {
      // fail silently
    }
  }
})

  app
    .use(router)
    .use(VueAxios, axios, router)
    .mount(`#${appID}`)
}

initializeApp()
